import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import "./style.css";
import IconMuite from "../SvgIcons/IconMuite";
import IconUnMuite from "../SvgIcons/IconUnMuite";
import IconUserIconsmall from "../SvgIcons/IconUserIconsmall";
import IconMinimize from "../SvgIcons/IconMinimize";
import IconRestore from "../SvgIcons/IconRestore";
import IconCallinComing from "../SvgIcons/IconCallinComing";
import IconCallEnd from "../SvgIcons/IconCallEnd";
import IconCallReject from "../SvgIcons/IconCallReject";
import { useNavigate } from "react-router-dom";
import { useSocket } from "../../context/SocketContext";

export default function CallModal({ callData, onCallEnd }) {
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [callState, setCallState] = useState("ringing");
  const [isMuted, setIsMuted] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [initialPos, setInitialPos] = useState({ top: 100, right: 20 });
  const [callStartTime, setCallStartTime] = useState(null); // Track call start
  const [elapsedTime, setElapsedTime] = useState(0); // Timer value
  const {setSocketFlag, incomingCall } = useSocket();
  // Set modal position
  useEffect(() => {
    const handleInitialPosition = () => {
      if (modalRef.current) {
        const modalHeight = modalRef.current.offsetHeight || 180;
        const top = window.innerHeight - modalHeight - 20;
        setInitialPos({ top, right: 20 });
      }
    };
    handleInitialPosition();
    window.addEventListener("resize", handleInitialPosition);
    return () => window.removeEventListener("resize", handleInitialPosition);
  }, []);

  // Reset modal on new call data
  useEffect(() => {
    setCallState("ringing");
    setIsAccepted(false);
    setIsMuted(false);
    setCallStartTime(null);
    setElapsedTime(0);
  }, [callData]);

  // Start timer when in call
  useEffect(() => {
    let interval;
    if (callState === "inCall") {
      setCallStartTime(Date.now());
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - callStartTime) / 1000));
      }, 1000);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [callState, callStartTime]);

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, "0");
    const mins = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  const handleAccept = () => {
    setIsAccepted(true);
    setCallState("waiting");

    // Simulate wait before inCall
    setTimeout(() => {
      setCallState("inCall");
    }, 3000);
  };

  const handleReject = () => {
    setCallState("ended");
    onCallEnd();
  };

  const handleTicketGeneration = () => {
    setTimeout(() => {
       const params = new URLSearchParams(incomingCall).toString();
      navigate(`/create-ticket?${params}`);
      setSocketFlag(prev => !prev);
    }, 500);
  }

  const handleMinimize = () => setCallState("minimized");
  const handleRestore = () => setCallState("inCall");
  const toggleMute = () => setIsMuted((prev) => !prev);

  if (callState === "ended") return null;

  return (
    <div ref={containerRef} style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999 }}>
      <motion.div
        ref={modalRef}
        className={`call-ui ${callState}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        drag
        dragConstraints={containerRef}
        dragElastic={0.3}
        dragMomentum={true}
        style={{
          position: "absolute",
          top: initialPos.top,
          right: initialPos.right,
          cursor: "grab",
          pointerEvents: "auto",
          willChange: "transform",
          transform: "translate3d(0, 0, 0)",
        }}
        whileTap={{ cursor: "grabbing" }}
      >
        <div className="call-content">
          {callState === "ringing" && !isAccepted && (
            <>
              <div className="user-icon"><IconUserIconsmall /></div>
              <h3>Incoming Call</h3>
              <div className="call-info text-center">
                <h4>{callData.caller_id_number || "Unknown"}</h4>
                {/* <p>To: {callData.call_to_number || "Unknown"}</p> */}
                {/* <p className="mb-0">{callData.start_stamp || "N/A"}</p> */}
                {callData.caller_name && <strong>{callData.caller_name || "Unknown"}</strong>}
                {/* <p>{callData.code || ""}</p> */}
                {callData.caller_designation && <p>{callData.caller_designation || ""}</p>}
                {callData.caller_cust_type && <p>{callData.caller_cust_type || ""}</p>}
                <p>{[callData.caller_city, callData.caller_state].filter(Boolean).join(",")}</p>
              </div>
              <button onClick={handleTicketGeneration} className="btn generateTicket"><img src='../Ticket-fff.png' style={{ width: 20 }} /> Generate Ticket</button>
              <div className="actions">
                <button onClick={handleAccept} className="btn accept"><IconCallinComing /> Accept</button>
                <button onClick={handleReject} className="btn reject btngradiant"><IconCallReject /> Reject</button>
                <button onClick={handleMinimize} className="tranparenteffect minimize"><IconMinimize /></button>
              </div>
            </>
          )}

          {callState === "waiting" && (
            <>
              <div className="user-icon"><IconUserIconsmall /></div>
              <h3>Waiting for Participant...</h3>
              <div className="call-info text-center">
                <p className="subtext">Please hold, connecting...</p>
              </div>
              <div className="actions">
                <button onClick={toggleMute} className="tranparenteffect mute-button position-relative">
                  {isMuted ? <IconMuite /> : <IconUnMuite />}
                </button>
                <button onClick={handleMinimize} className="tranparenteffect minimize"><IconMinimize /></button>
                <button onClick={handleReject} className="btn btngradiant reject"><IconCallEnd /> End Call</button>
              </div>
            </>
          )}

          {callState === "inCall" && (
            <>
              <div className="user-icon"><IconUserIconsmall /></div>
              <h3>On Call with</h3>
              <div className="call-info responseoncall text-center">
                <h4>{callData.caller_id_number || "Unknown"}</h4>
                {callData.caller_name && <strong>{callData.caller_name || "Unknown"}</strong>}
                {/* <p>{callData.start_stamp || "N/A"}</p> */}
                {callData.caller_designation && <p>{callData.caller_designation || ""}</p>}
                {callData.caller_cust_type && <p>{callData.caller_cust_type || ""}</p>}
                <p>{[callData.caller_city, callData.caller_state].filter(Boolean).join(",")}</p>
                <p className="mb-0">{formatDuration(elapsedTime)}</p>
              </div>
              <div className="actions">
                <button onClick={toggleMute} className="tranparenteffect mute-button position-relative">
                  {isMuted ? <IconMuite /> : <IconUnMuite />}
                </button>
                <button onClick={handleMinimize} className="tranparenteffect minimize"><IconMinimize /></button>
                <button onClick={handleReject} className="btn btngradiant reject"><IconCallEnd /> End Call</button>
              </div>
            </>
          )}

          {callState === "minimized" && (
            <div className="minimized-view">
              <button onClick={handleRestore} className="tranparenteffect minimize"><IconRestore /></button>
              <h3>On Call with </h3>
              <div className="call-info responseoncall text-center">
                <strong>{callData.caller_id_number?.trim() || callData.caller_name?.trim() || "Unknown"}</strong>
                <p className="mb-0">{formatDuration(elapsedTime)}</p>
              </div>
              <div className="actions">
                <button onClick={toggleMute} className="tranparenteffect mute-button position-relative">
                  {isMuted ? <IconMuite /> : <IconUnMuite />}
                </button>
                <button onClick={handleReject} className="btn btngradiant reject"><IconCallEnd /> End</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
